import { formatAsRegionalPhoneCode, isValidCnpj as isValidCnpjShared, isValidCpf, sanitizeAccountMaskAware, } from '@ibk/shared-utils';
import { AccountType, AccountTypeAcronym, AccountTypeDescription, BillType, FinancialInstructionOperationType, FinancialInstructionSource, InstructionCurrency, PixKeyType, PixKeyTypeDescription, PixType, TransactionsServiceDocumentType, TransactionsServiceKeyType, } from '../enums';
export function getPixKeyConfirmationType(selectedKeyType) {
    if (!selectedKeyType) {
        throw new Error('selectedKeyType is required for confirmation');
    }
    return selectedKeyType === PixType.EMAIL ? 'EMAIL' : 'PHONE';
}
export function toPixKeyTypeDescription(key, value, cnpjHelper) {
    if (!key)
        return;
    const isCnpj = (v) => (cnpjHelper?.isValidCnpj ?? isValidCnpjShared)(v);
    return {
        [PixType.TAX_IDENTIFIER]: isCnpj(value) ? PixKeyTypeDescription.CNPJ : PixKeyTypeDescription.CPF,
        [PixKeyTypeDescription.CNPJ]: PixKeyTypeDescription.CNPJ,
        [PixKeyTypeDescription.CPF]: PixKeyTypeDescription.CPF,
        [PixType.EMAIL]: PixKeyTypeDescription.EMAIL,
        [PixKeyTypeDescription.EMAIL]: PixKeyTypeDescription.EMAIL,
        [PixType.PHONE]: PixKeyTypeDescription.PHONE,
        [PixKeyTypeDescription.PHONE]: PixKeyTypeDescription.PHONE,
        [PixType.RANDOM]: PixKeyTypeDescription.RANDOM,
        [PixKeyTypeDescription.RANDOM]: PixKeyTypeDescription.RANDOM,
        [PixType.AGENCY]: PixKeyTypeDescription.AGENCY,
        [PixKeyTypeDescription.AGENCY]: PixKeyTypeDescription.AGENCY,
        [PixType.PORTABILITY]: PixKeyTypeDescription.PORTABILITY,
        [PixKeyTypeDescription.PORTABILITY]: PixKeyTypeDescription.PORTABILITY,
        [PixType.QR_CODE]: PixKeyTypeDescription.QR_CODE,
        [PixKeyTypeDescription.QR_CODE]: PixKeyTypeDescription.QR_CODE,
    }[key];
}
export function toAccountTypeAcronym(key) {
    if (!key)
        return;
    return {
        [AccountType.CURRENT]: AccountTypeAcronym.CHECKING_ACCOUNT,
        [AccountTypeDescription.CURRENT]: AccountTypeAcronym.CHECKING_ACCOUNT,
        [AccountType.IP]: AccountTypeAcronym.CHECKING_ACCOUNT,
        [AccountTypeDescription.IP]: AccountTypeAcronym.CHECKING_ACCOUNT,
        [AccountType.PAYMENTS]: AccountTypeAcronym.PAYMENT_ACCOUNT,
        [AccountTypeDescription.PAYMENTS]: AccountTypeAcronym.PAYMENT_ACCOUNT,
        [AccountType.SAVINGS]: AccountTypeAcronym.SAVING_ACCOUNT,
        [AccountTypeDescription.SAVINGS]: AccountTypeAcronym.SAVING_ACCOUNT,
    }[key];
}
export function toPixType(key) {
    if (!key)
        return;
    return {
        [PixKeyType.CPF]: PixType.TAX_IDENTIFIER,
        [PixKeyTypeDescription.CPF]: PixType.TAX_IDENTIFIER,
        [PixKeyType.CNPJ]: PixType.TAX_IDENTIFIER,
        [PixKeyTypeDescription.CNPJ]: PixType.TAX_IDENTIFIER,
        [PixKeyType.EMAIL]: PixType.EMAIL,
        [PixKeyTypeDescription.EMAIL]: PixType.EMAIL,
        [PixKeyType.PHONE]: PixType.PHONE,
        [PixKeyTypeDescription.PHONE]: PixType.PHONE,
        [PixKeyType.RANDOM]: PixType.RANDOM,
        [PixKeyTypeDescription.RANDOM]: PixType.RANDOM,
        [PixKeyType.BANK_ACCOUNT]: PixType.AGENCY,
        [PixKeyTypeDescription.AGENCY]: PixType.AGENCY,
    }[key];
}
export function toBatchPayableGenericPixKeys(values) {
    return values
        .map(value => {
        const paymentDetails = value.employeePaymentDetailsInfo?.paymentDetails;
        const base = {
            accountPayablesId: value.id,
            amount: value.amount,
            receiverName: value.title,
            receiverDocumentNumber: value.employeeDetails?.documentNumber,
        };
        const pixDetails = paymentDetails?.type === BillType.PIX ? paymentDetails.details : undefined;
        const accountDetails = paymentDetails?.type === BillType.ACCOUNT ? paymentDetails.details : undefined;
        const keyType = pixDetails ? pixDetails.keyType : value.pixKeyType;
        const isBankAccount = !!accountDetails || keyType === PixKeyType.BANK_ACCOUNT;
        if (isBankAccount) {
            const counterparty = accountDetails ?? pixDetails?.counterparty ?? value.counterparty;
            return {
                ...base,
                account: counterparty?.account,
                accountTypeAcronym: counterparty?.accountType,
                bankCode: counterparty?.bank,
                branch: counterparty?.branch,
                type: toPixKeyTypeDescription(PixType.AGENCY),
            };
        }
        if (pixDetails || keyType !== undefined) {
            const key = pixDetails ? pixDetails.key : value.keyPayment;
            return {
                ...base,
                key,
                type: toPixKeyTypeDescription(toPixType(keyType)),
            };
        }
        return null;
    })
        .filter((value) => value !== null);
}
export function toPixBatchItems(batch, scheduledTo, helpers) {
    return batch.map(key => {
        const type = toPixKeyTypeDescription(key.type, key.key, helpers);
        const counterpartyDocumentType = isValidCpf(key.receiverDocumentNumber ?? '')
            ? TransactionsServiceDocumentType.CPF
            : TransactionsServiceDocumentType.CNPJ;
        const commonTo = {
            description: key.description ?? '',
            counterpartyName: key.receiverName ?? '',
            counterpartyDocumentType,
            counterpartyDocument: key.receiverDocumentNumber ?? '',
        };
        if (type === TransactionsServiceKeyType.AGENCY) {
            return {
                operationType: FinancialInstructionOperationType.PIX,
                amount: key.amount,
                currency: InstructionCurrency.BRL,
                source: FinancialInstructionSource.FINTECH_IBK,
                to: {
                    ...commonTo,
                    keyType: TransactionsServiceKeyType.AGENCY,
                    counterpartyBankAccount: sanitizeAccountMaskAware(key.account),
                    counterpartyBankBranch: key.branch,
                    counterpartyAccountType: key.accountTypeAcronym,
                    counterpartyBankCode: key.bankCode,
                    counterpartyBankName: key.bankName,
                },
                accountpayablesId: key.accountPayablesId,
                confirmDuplicate: key.confirmDuplicate,
                scheduledTo,
            };
        }
        return {
            operationType: FinancialInstructionOperationType.PIX,
            amount: key.amount,
            currency: InstructionCurrency.BRL,
            source: FinancialInstructionSource.FINTECH_IBK,
            to: {
                ...commonTo,
                keyType: type,
                key: type === TransactionsServiceKeyType.PHONE ? formatAsRegionalPhoneCode(key.key) : key.key,
                qrCodeType: key.qrCodeType,
                transactionIdentification: key.transactionIdentification,
            },
            accountpayablesId: key.accountPayablesId,
            confirmDuplicate: key.confirmDuplicate,
            scheduledTo,
        };
    });
}
export function toPixKeyType(key, value, cnpjHelper) {
    if (!key)
        return;
    const isCnpj = (v) => (cnpjHelper?.isValidCnpj ?? isValidCnpjShared)(v);
    return {
        [PixType.TAX_IDENTIFIER]: isCnpj(value) ? PixKeyType.CNPJ : PixKeyType.CPF,
        [PixType.EMAIL]: PixKeyType.EMAIL,
        [PixType.PHONE]: PixKeyType.PHONE,
        [PixType.RANDOM]: PixKeyType.RANDOM,
        [PixType.AGENCY]: PixKeyType.BANK_ACCOUNT,
    }[key];
}
